// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { BarChart2, RefreshCw } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { getUsage, ApiError } from '@/lib/api'
import type { OrgUsage } from '@/types'
import { Button } from '@/components/ui/button'
import AccessDenied from '@/components/AccessDenied'

type PageState = 'loading' | 'unavailable' | 'forbidden' | 'empty' | 'table'

function formatActivity(lastActivityAt: string | null): string {
  if (!lastActivityAt) return '—'
  try {
    return format(parseISO(lastActivityAt), 'MMM d, yyyy')
  } catch {
    return '—'
  }
}

function SkeletonRow() {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="px-4 py-3"><div className="h-4 w-40 rounded shimmer" /></td>
      <td className="px-4 py-3"><div className="h-4 w-12 rounded shimmer" /></td>
      <td className="px-4 py-3"><div className="h-4 w-12 rounded shimmer" /></td>
      <td className="px-4 py-3"><div className="h-4 w-24 rounded shimmer" /></td>
    </tr>
  )
}

export default function UsagePage() {
  const { t } = useTranslation()

  const [orgs, setOrgs] = useState<OrgUsage[]>([])
  const [pageState, setPageState] = useState<PageState>('loading')

  const fetchUsage = useCallback(async () => {
    setPageState('loading')
    try {
      const data = await getUsage()
      setOrgs(data.orgs)
      setPageState(data.orgs.length === 0 ? 'empty' : 'table')
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setPageState('forbidden')
      } else {
        // Endpoint is spec-only — any ApiError renders a calm unavailable state,
        // not a scary error. This is intentional per product spec.
        setPageState('unavailable')
      }
    }
  }, [])

  useEffect(() => { void fetchUsage() }, [fetchUsage])

  if (pageState === 'forbidden') {
    return <AccessDenied />
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#012031] via-[#033C85] to-[#006EF9]">
        <div className="absolute -bottom-16 -right-16 w-[220px] h-[220px] rounded-full bg-[#02C7C3]/20 blur-3xl pointer-events-none" />
        <div className="relative z-10 px-6 py-6 flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-white/15 border border-white/25 flex items-center justify-center shrink-0">
            <BarChart2 className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white leading-tight">{t('usage.title')}</h1>
        </div>
      </div>

      {/* Loading skeleton */}
      {pageState === 'loading' && (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <table className="w-full" role="table">
            <tbody>
              {Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)}
            </tbody>
          </table>
        </div>
      )}

      {/* Unavailable / spec-only — calm empty state, not a scary error */}
      {pageState === 'unavailable' && (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center">
            <BarChart2 className="h-7 w-7 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-foreground">{t('usage.unavailable')}</p>
          <p className="text-xs text-muted-foreground max-w-xs">{t('usage.unavailableBody')}</p>
          <Button variant="outline" size="sm" onClick={() => void fetchUsage()} className="mt-1 gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" />
            {t('usage.retry')}
          </Button>
        </div>
      )}

      {/* Empty state (successful response but no orgs) */}
      {pageState === 'empty' && (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center">
            <BarChart2 className="h-7 w-7 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-foreground">{t('usage.empty')}</p>
        </div>
      )}

      {/* Table */}
      {pageState === 'table' && (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" role="table">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {t('usage.colOrganization')}
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {t('usage.colIntents')}
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {t('usage.colActiveMembers')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {t('usage.colLastActivity')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {orgs.map(org => (
                  <tr key={org.orgId} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{org.displayName}</td>
                    <td className="px-4 py-3 text-sm text-right tabular-nums text-foreground">{org.intents}</td>
                    <td className="px-4 py-3 text-sm text-right tabular-nums text-foreground">{org.activeMembers}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{formatActivity(org.lastActivityAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
